import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";
import TableContainer from "../../../../Components/Common/TableContainer";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

// Super Admin > User / Company Mapping (Task 24428).
//
// The team list in company settings answers "who is in THIS company". Support
// needs the other direction - "which companies is this person even in" - which
// is what POST /companytouser/list/mapping returns, across every company on the
// platform. CompanyToUser, User and Company all live in the master database, so
// it is one query.
//
// There is no dated access window on CompanyToUser: no AccessStartDateTime /
// AccessEndDateTime pair exists. What there is:
//   CreatedDateTime  - when they were added        (de facto start)
//   DeletedDateTime  - when access was stopped     (de facto end, soft delete)
//   FlagActive       - derived from whether they still hold a live assigned
//                      SubscriptionLicence in that company (delta 20260817)
//   UserLockedFlag   - a reversible suspension
// So access is stopped by an action, not by a date it lapses on.
const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive (no licence)" },
  { value: "locked", label: "Locked" },
  { value: "removed", label: "Stopped" },
];

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const rowStatus = (r) => {
  if (r?.CompanyToUser_DeletedDateTime) return "removed";
  if (Number(r?.CompanyToUser_UserLockedFlag) === 1) return "locked";
  if (Number(r?.CompanyToUser_FlagActive) === 0) return "inactive";
  return "active";
};

const UserCompanyMapping = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [includeSupport, setIncludeSupport] = useState(false);
  const [includeRemoved, setIncludeRemoved] = useState(false);

  // Stop-access confirmation
  const [pendingStop, setPendingStop] = useState(null);
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await FetchAPI("/companytouser/list/mapping", "POST", [
        {
          // Sent every time, including empty, so clearing the box actually
          // clears the filter rather than leaving the last one applied.
          Search: search.trim(),
          FlagIncludeSupportStaff: includeSupport ? 1 : 0,
          FlagIncludeRemoved: includeRemoved ? 1 : 0,
        },
      ]);
      if (Number(res?.status) === 200) {
        setRows(res?.companytouser_list || []);
        setSummary(res?.summary || null);
      } else {
        setRows([]);
        setSummary(null);
      }
    } catch (e) {
      setRows([]);
      setSummary(null);
    }
    setLoading(false);
  };

  // Search runs on the server (it is the only thing that has the whole
  // platform), so every server-side filter goes through one debounced effect -
  // two effects would fire two requests on mount.
  useEffect(() => {
    const timer = setTimeout(() => load(), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, includeSupport, includeRemoved]);

  // Company and status narrow what is already in hand, so they are instant.
  const companyOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (!r?.Company_Id || seen.has(String(r.Company_Id))) return;
      seen.set(
        String(r.Company_Id),
        r?.Company_BusinessName || `Company #${r.Company_Id}`
      );
    });
    return Array.from(seen, ([value, label]) => ({ value, label })).sort(
      (a, b) => a.label.localeCompare(b.label)
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (companyFilter && String(r?.Company_Id) !== String(companyFilter)) {
        return false;
      }
      if (statusFilter && rowStatus(r) !== statusFilter) return false;
      return true;
    });
  }, [rows, companyFilter, statusFilter]);

  const activeFilterCount = [companyFilter, statusFilter].filter(Boolean).length;

  const clearFilters = () => {
    setCompanyFilter("");
    setStatusFilter("");
  };

  // Stopping access is the soft delete on CompanyToUser - it stamps
  // DeletedDateTime / DeletedByUserId, so the row stays for the audit and
  // reappears under "Include stopped".
  const stopAccess = async () => {
    if (!pendingStop?.CompanyToUser_Id) return;
    setWorking(true);
    try {
      const res = await FetchAPI("/companytouser/delete", "DELETE", [
        pendingStop.CompanyToUser_Id,
      ]);
      if (Number(res?.status) === 200) {
        toast.success(
          `Access stopped for ${pendingStop.User_UserFullName || "that user"} in ${
            pendingStop.Company_BusinessName || "that company"
          }`
        );
        setPendingStop(null);
        await load();
      } else {
        toast.error(res?.message || "Could not stop that access");
      }
    } catch (e) {
      toast.error("Could not stop that access");
    }
    setWorking(false);
  };

  const setLocked = async (row, locked) => {
    try {
      const res = await FetchAPI(
        locked ? "/companytouser/lockuser" : "/companytouser/unlockuser",
        "POST",
        [{ CompanyToUser_Id: row.CompanyToUser_Id, User_Id: row.User_Id }]
      );
      if (Number(res?.status) === 200) {
        toast.success(locked ? "User locked" : "User unlocked");
        await load();
      } else {
        toast.error(res?.message || "Could not change that user's lock");
      }
    } catch (e) {
      toast.error("Could not change that user's lock");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "User",
        accessor: "User_UserFullName",
        filterable: false,
        Cell: (cell) => {
          const r = cell.row.original;
          return (
            <div>
              <div>
                {r?.User_UserFullName || (
                  <span className="text-muted fst-italic">Unknown</span>
                )}
              </div>
              <div className="text-muted fs-12">{r?.User_Email || ""}</div>
            </div>
          );
        },
      },
      {
        Header: "Company",
        accessor: "Company_BusinessName",
        filterable: false,
        Cell: (cell) => {
          const r = cell.row.original;
          return (
            <div>
              <div>
                {r?.Company_BusinessName || (
                  <span className="text-muted fst-italic">Unknown</span>
                )}
              </div>
              <div className="text-muted fs-12">#{r?.Company_Id}</div>
            </div>
          );
        },
      },
      {
        Header: "Access From",
        accessor: "CompanyToUser_CreatedDateTime",
        filterable: false,
        Cell: (cell) => fmtDate(cell.row.original?.CompanyToUser_CreatedDateTime),
      },
      {
        Header: "Access Stopped",
        accessor: "CompanyToUser_DeletedDateTime",
        filterable: false,
        Cell: (cell) => fmtDate(cell.row.original?.CompanyToUser_DeletedDateTime),
      },
      {
        Header: "Last Login",
        accessor: "CompanyToUser_LastLoginDateTime",
        filterable: false,
        Cell: (cell) => fmtDate(cell.row.original?.CompanyToUser_LastLoginDateTime),
      },
      {
        Header: "Last Active",
        accessor: "CompanyToUser_LastActiveDateTime",
        filterable: false,
        Cell: (cell) =>
          fmtDate(cell.row.original?.CompanyToUser_LastActiveDateTime),
      },
      {
        Header: "Status",
        accessor: "CompanyToUser_FlagActive",
        filterable: false,
        Cell: (cell) => {
          const r = cell.row.original;
          const status = rowStatus(r);
          if (status === "removed") {
            return (
              <Badge
                color="secondary"
                title={fmtDate(r?.CompanyToUser_DeletedDateTime)}
              >
                Stopped
              </Badge>
            );
          }
          if (status === "locked") {
            return (
              <Badge
                color="danger"
                title={fmtDate(r?.CompanyToUser_UserLockedDateTime)}
              >
                Locked
              </Badge>
            );
          }
          if (status === "inactive") {
            return (
              <Badge color="warning" title="No live assigned licence here">
                Inactive
              </Badge>
            );
          }
          return <Badge color="success">Active</Badge>;
        },
      },
      {
        Header: "Action",
        filterable: false,
        Cell: (cell) => {
          const r = cell.row.original;
          // A stopped mapping has nothing left to act on - re-adding someone is
          // a company-side invite, not a super-admin toggle.
          if (r?.CompanyToUser_DeletedDateTime) {
            return <span className="text-muted fs-12">—</span>;
          }
          const locked = Number(r?.CompanyToUser_UserLockedFlag) === 1;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem onClick={() => setLocked(r, !locked)}>
                  <i
                    className={`${
                      locked ? "ri-lock-unlock-line" : "ri-lock-line"
                    } align-bottom me-2 text-muted`}
                  ></i>
                  {locked ? "Unlock user" : "Lock user"}
                </DropdownItem>
                <DropdownItem onClick={() => setPendingStop(r)}>
                  <i className="ri-logout-box-line align-bottom me-2 text-muted"></i>
                  Stop access
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={Boolean(pendingStop)}
        loading={working}
        onCloseClick={() => setPendingStop(null)}
        onDeleteClick={stopAccess}
        customText={
          pendingStop
            ? `Stop ${pendingStop.User_UserFullName || "this user"}'s access to ${
                pendingStop.Company_BusinessName || "this company"
              }? They keep their login but lose this company. The mapping is kept for the audit.`
            : undefined
        }
        customButtonText="Stop access"
      />

      <Card>
        <CardHeader className="border-0">
          <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
            <div>
              <h5 className="mb-1">User / Company Mapping</h5>
              <p className="text-muted mb-0">
                Which users belong to which companies, across the whole
                platform.
              </p>
            </div>
            <Button color="light" size="sm" onClick={load} disabled={loading}>
              <i className="ri-refresh-line align-middle me-1" />
              Refresh
            </Button>
          </div>

          <Row className="g-2 align-items-end">
            <Col sm={6} md={3}>
              <label className="form-label mb-1 fs-13">Search</label>
              <Input
                type="search"
                placeholder="Name, email or company…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col sm={6} md={3}>
              <label className="form-label mb-1 fs-13">Company</label>
              <Input
                type="select"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <option value="">All companies</option>
                {companyOptions.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col sm={6} md={2}>
              <label className="form-label mb-1 fs-13">Status</label>
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value || "all"} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col sm={6} md={2}>
              <button
                className="btn btn-light w-100"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear
              </button>
            </Col>
            <Col md={2}>
              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="ucm-support"
                  checked={includeSupport}
                  onChange={(e) => setIncludeSupport(e.target.checked)}
                />
                <label
                  className="form-check-label fs-13"
                  htmlFor="ucm-support"
                >
                  Support logins
                </label>
              </div>
              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="ucm-removed"
                  checked={includeRemoved}
                  onChange={(e) => setIncludeRemoved(e.target.checked)}
                />
                <label
                  className="form-check-label fs-13"
                  htmlFor="ucm-removed"
                >
                  Include stopped
                </label>
              </div>
            </Col>
          </Row>

          {summary && !loading && (
            <p className="text-muted mb-0 mt-3 fs-13">
              Showing {filteredRows.length} of {summary.RowQty} mapping
              {summary.RowQty === 1 ? "" : "s"} — {summary.UserQty} user
              {summary.UserQty === 1 ? "" : "s"} across {summary.CompanyQty}{" "}
              compan{summary.CompanyQty === 1 ? "y" : "ies"}.
              {Number(summary.FlagTruncated) === 1 && (
                <span className="text-warning ms-1">
                  Showing the first {summary.Limit} — narrow the search to see
                  the rest.
                </span>
              )}
            </p>
          )}
        </CardHeader>

        <CardBody className="pt-0">
          {loading ? (
            <LoadingSpinner />
          ) : filteredRows.length > 0 ? (
            <TableContainer
              columns={columns}
              data={filteredRows}
              isGlobalFilter={false}
              isAddUserList={false}
              customPageSize={25}
              className="custom-header-css"
              divClass="table-responsive table-card mb-0"
              tableClass="align-middle table-nowrap"
              theadClass="text-muted"
              isLeadsFilter={false}
            />
          ) : (
            <NoDataContent
              titleText={
                activeFilterCount > 0 || search
                  ? "No mappings match these filters."
                  : "No user / company mappings found."
              }
              paragraphText={
                activeFilterCount > 0 || search
                  ? "Widen or clear the search, company and status filters."
                  : "Nothing has been mapped yet."
              }
              useButton={false}
            />
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default UserCompanyMapping;
